import { useQuery } from "@tanstack/react-query"
import { CheckCircle2, Clock } from "lucide-react"
import { Karte, Leer } from "@/components/ui"
import { api, fehlertext } from "@/lib/api"
import { cn, nf0 } from "@/lib/format"

type Status = "da" | "eingesprungen" | "fehlt" | "nicht erschienen" | "gewechselt"

interface Person { name: string; stelle: string; status: Status }

interface Schicht {
  nr: number; name: string; modell: string; beginn: string; ende: string; tag: string
  arbeitstag: boolean; kalender: string; geprueft: boolean
  zahlen: { soll: number; da: number; fehlt: number; eingesprungen: number; gewechselt: number }
  personen: Person[]
}

interface Antwort { eingerichtet: boolean; stand: string | null; schichten: Schicht[] }

const tagText = (iso: string) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })

function Zahl({ wert, text, warn }: { wert: number; text: string; warn?: boolean }) {
  return (
    <div className="grid leading-tight">
      <b className={cn("text-xl tabular", warn && wert > 0 && "text-crit")}>{nf0.format(wert)}</b>
      <span className="text-[11.5px] text-ink2">{text}</span>
    </div>
  )
}

function Namen({ titel, personen }: { titel: string; personen: Person[] }) {
  if (!personen.length) return null
  return (
    <details className="text-[13px]">
      <summary className="cursor-pointer text-ink2">{`${titel} (${personen.length})`}</summary>
      <ul className="m-0 mt-1.5 grid gap-0.5 pl-4 sm:grid-cols-2">
        {personen.map((p) => (
          <li key={p.name + p.stelle}>
            {p.name}
            {p.status === "eingesprungen" && <span className="ml-1.5 rounded bg-accent/10 px-1 text-[11px] text-accent-strong">eingesprungen</span>}
            {p.stelle && <span className="ml-1.5 text-[11.5px] text-muted">{p.stelle}</span>}
          </li>
        ))}
      </ul>
    </details>
  )
}

function SchichtBlock({ s, heute }: { s: Schicht; heute: string }) {
  const fehlen = s.personen.filter((p) => p.status === "fehlt" || p.status === "nicht erschienen")
  const anwesend = s.personen.filter((p) => p.status === "da" || p.status === "eingesprungen")
  const gewechselt = s.personen.filter((p) => p.status === "gewechselt")
  return (
    <div className="grid gap-2.5 border-t border-grid pt-3 first:border-t-0 first:pt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-x-2">
          <b className="text-[15px]">{s.name}</b>
          <span className="tabular text-[12.5px] text-ink2">{`${s.beginn}–${s.ende}`}</span>
          {s.tag !== heute && <span className="text-[12px] text-ink2">{`Schichttag ${tagText(s.tag)}`}</span>}
          <span className="text-[11.5px] text-muted">{s.modell}</span>
        </div>
        {s.arbeitstag && (
          s.geprueft ? (
            <span className="inline-flex items-center gap-1 text-[12px] text-good"><CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />Anwesenheit geprüft</span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[12px] text-muted" title="Fehlende sind erst vollständig, wenn der Schichtführer die Anwesenheit geprüft hat.">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />noch nicht geprüft
            </span>
          )
        )}
      </div>
      {!s.arbeitstag ? (
        <p className="m-0 text-[13px] text-ink2">{`Kein Arbeitstag${s.kalender ? ` (${s.kalender})` : ""}.`}</p>
      ) : s.zahlen.soll === 0 ? (
        <p className="m-0 text-[13px] text-ink2">Für diese Schicht ist niemand eingeplant.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            <Zahl wert={s.zahlen.soll} text="eingeplant" />
            <Zahl wert={s.zahlen.da} text="anwesend" />
            <Zahl wert={s.zahlen.fehlt} text="fehlen" warn />
          </div>
          {fehlen.length > 0 && (
            <ul className="m-0 grid gap-1 rounded-lg bg-crit/10 px-3 py-2 text-[13px]">
              {fehlen.map((p) => (
                <li key={p.name + p.stelle} className="flex flex-wrap items-baseline gap-x-2">
                  <b>{p.name}</b>
                  <span className={cn(p.status === "nicht erschienen" ? "font-medium text-crit" : "text-ink2")}>{p.status}</span>
                  {p.stelle && <span className="text-[11.5px] text-muted">{p.stelle}</span>}
                </li>
              ))}
            </ul>
          )}
          <Namen titel="Anwesend" personen={anwesend} />
          <Namen titel="In eine andere Schicht gewechselt" personen={gewechselt} />
        </>
      )}
    </div>
  )
}

/** Aktuelle Schicht nach Plan (Ninox 15-Schichtplanung): wer eingeplant ist, wer fehlt. */
export function AktuelleSchicht() {
  const abfrage = useQuery({
    queryKey: ["schichtplan"],
    queryFn: async () => (await api.get<Antwort>("/schichtplan/aktuell")).data,
    refetchInterval: 60_000,
  })
  const d = abfrage.data
  const heute = new Date().toLocaleDateString("sv-SE")
  return (
    <Karte
      titel="Aktuelle Schicht"
      unter="Nach Plan aus der Schichtplanung. „Fehlt“ heißt: eine Abweichung ist eingetragen; den Grund zeigt nur das Anwesenheitsboard der Schichtplanung in Ninox."
      rechts={d?.stand ? <span className="text-[12px] text-ink2">{`Stand ${d.stand.slice(11, 16)} Uhr`}</span> : undefined}
    >
      {abfrage.isError ? (
        <p className="m-0 text-sm text-crit">{fehlertext(abfrage.error, "Die Schichtplanung ist gerade nicht erreichbar.")}</p>
      ) : !d ? (
        <Leer>Wird geladen …</Leer>
      ) : !d.eingerichtet ? (
        <Leer>Die Schichtplanung ist noch nicht angebunden.</Leer>
      ) : d.schichten.length === 0 ? (
        <Leer>Gerade läuft keine Schicht.</Leer>
      ) : (
        <div className="grid gap-3">
          {d.schichten.map((s) => <SchichtBlock key={`${s.nr}-${s.tag}`} s={s} heute={heute} />)}
        </div>
      )}
    </Karte>
  )
}
